/**
 * Normalizer for HubSpot "school" records returned by
 * GET /api/hubspot/schools?q=
 *
 * The backend brokers HubSpot company records, and the exact property names
 * can vary (HubSpot properties, ClickUp mirrors, or a flattened DTO). Rather
 * than hard-couple the UI to one shape, we defensively map the common
 * candidates here and keep one clean internal shape:
 *
 *   {
 *     id, name, city, state, location,
 *     colors: [{ hex, label }],   // verified brand colors
 *     logoUrl,                    // OWNED HubSpot-hosted logo
 *     raw                         // original record, for debugging
 *   }
 *
 * Update the candidate lists here (not the components) when the backend DTO
 * is finalized. See CLAUDE.md → "School record shape".
 */

const pick = (obj, keys) => {
  for (const k of keys) {
    const v = get(obj, k)
    if (v !== undefined && v !== null && v !== '') return v
  }
  return undefined
}

// Supports dotted paths like "properties.name".
const get = (obj, path) => {
  if (!obj) return undefined
  if (path.indexOf('.') === -1) return obj[path]
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj)
}

const HEX_RE = /#?[0-9a-f]{6}\b|#?[0-9a-f]{3}\b/gi

function toHex(value) {
  if (!value) return null
  let v = String(value).trim()
  if (!v) return null
  if (v[0] !== '#') v = `#${v}`
  // Expand shorthand #abc -> #aabbcc
  if (/^#[0-9a-f]{3}$/i.test(v)) {
    v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`
  }
  return /^#[0-9a-f]{6}$/i.test(v) ? v.toLowerCase() : null
}

/** Parse colors from arrays, objects, or delimited strings. */
function normalizeColors(record) {
  const raw =
    pick(record, [
      'colors',
      'brandColors',
      'brand_colors',
      'school_colors',
      'schoolColors',
      'properties.school_colors',
      'properties.brand_colors',
      'palette',
    ]) ?? null

  const out = []
  const seen = new Set()
  const push = (hex, label) => {
    const h = toHex(hex)
    if (h && !seen.has(h)) {
      seen.add(h)
      out.push({ hex: h, label: label || '' })
    }
  }

  if (Array.isArray(raw)) {
    for (const c of raw) {
      if (typeof c === 'string') push(c)
      else if (c && typeof c === 'object')
        push(c.hex || c.value || c.color || c.code, c.label || c.name)
    }
  } else if (raw && typeof raw === 'object') {
    for (const [label, hex] of Object.entries(raw)) push(hex, label)
  } else if (typeof raw === 'string') {
    const matches = raw.match(HEX_RE)
    if (matches) matches.forEach((m) => push(m))
  }

  // Fallback: explicit primary/secondary properties.
  push(pick(record, ['primary_color', 'primaryColor', 'color_primary']), 'Primary')
  push(
    pick(record, ['secondary_color', 'secondaryColor', 'color_secondary']),
    'Secondary'
  )

  return out
}

export function normalizeSchool(record) {
  if (!record || typeof record !== 'object') return null

  const id =
    pick(record, ['id', 'hs_object_id', 'objectId', 'recordId', 'schoolId']) ??
    pick(record, ['properties.hs_object_id'])

  const name =
    pick(record, [
      'name',
      'schoolName',
      'school_name',
      'companyName',
      'company',
      'properties.name',
      'label',
      'title',
    ]) ?? 'Unnamed school'

  const city = pick(record, ['city', 'properties.city', 'town'])
  const state = pick(record, [
    'state',
    'stateProvince',
    'state_province',
    'properties.state',
    'region',
  ])

  const location = [city, state].filter(Boolean).join(', ')

  const logoUrl = pick(record, [
    'logoUrl',
    'logo_url',
    'logo',
    'logoImage',
    'crest',
    'crestUrl',
    'mascotLogo',
    'primaryLogo',
    'primary_logo',
    'ownedLogo',
    'owned_logo',
    'hubspotLogo',
    'properties.logo_url',
    'properties.logo',
    'imageUrl',
    'image_url',
  ])

  const mascot = pick(record, [
    'mascot',
    'sr_mascot',
    'properties.sr_mascot',
    'nickname',
    'team_name',
  ])

  return {
    id: id != null ? String(id) : name,
    schoolTeamCoverage: record.schoolTeamCoverage || null,
    name: String(name),
    city: city ? String(city) : '',
    state: state ? String(state) : '',
    location,
    mascot: mascot ? String(mascot) : '',
    colors: normalizeColors(record),
    logoUrl: logoUrl ? String(logoUrl) : '',
    raw: record,
  }
}

/** Normalize a schools response into an array of school objects. */
export function normalizeSchools(response) {
  const list = Array.isArray(response)
    ? response
    : response?.schools ||
      response?.results ||
      response?.data ||
      response?.items ||
      []
  return list.map(normalizeSchool).filter(Boolean)
}
